import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';

import { DateSelect } from './DateSelect';
import { FrequencySelect, FrequencyValueType } from './FrequencySelect';
import { AvailabilityCenterActivityType_VOLUNTEER_RecurringActivityType_schedule_recurrences as ActivityRecurrenceType } from '../__generated__/AvailabilityCenterActivityType';
import {
  BeginOptionsEnum,
  CUSTOM_FREQUENCY_OPTION,
  CustomFrequencyValue,
  DEFAULT_CUSTOM_FREQUENCY,
  DAILY_BEGIN_OPTIONS,
  DAILY_OPTIONS,
  DAY_OPTIONS,
  EVERY_WEEKEND_OPTIONS,
  FREQUENCY_ENUM,
  LengthOptionEnum,
  LENGTH_OPTIONS,
  MONTHLY_BEGIN_OPTIONS,
  MONTHLY_OPTIONS,
  WEEKDAYS,
  WEEKENDS,
  WEEKLY_BEGIN_OPTIONS,
  WEEKLY_OPTIONS,
  YEARLY_BEGIN_OPTIONS,
  YEARLY_LENGTH_OPTIONS,
  YEARLY_OPTIONS,
} from './consts';
import { RecurrenceEnum } from '../../../__generated__/globalTypes';
import {
  describeCustomFrequency,
  filterByBegin,
  filterByCustomFrequency,
  filterByDay,
  filterByFrequence,
  filterInRange,
} from './helper';
import { CustomFrequencyDialog } from './CustomFrequencyDialog';
import { Legend } from './Legend';
import { useDeepEffect } from '../../../hooks/useDeepEffect';

interface Props {
  startDate: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  recurrences: ActivityRecurrenceType[];
  occurrences: string[];
  availabilities: string[];
  unavailabilities: string[];
  availabilityRestrictionDays?: number | null;
  allowCustomFrequency?: boolean;
}

export function DateAndFrequencySelect({
  startDate,
  value,
  onChange,
  recurrences,
  occurrences,
  availabilities,
  unavailabilities,
  availabilityRestrictionDays,
  allowCustomFrequency,
}: Props) {
  const { __typename: recurrenceType } = recurrences[0];

  const isWeekDay = React.useMemo(() => {
    const recurrence = recurrences[0];
    if (recurrence.__typename !== 'WeeklyRecurrenceType') {
      return false;
    }

    return recurrence.days.length === WEEKDAYS.length && recurrence.days.every((day) => WEEKDAYS.includes(day));
  }, [recurrences]);

  const isWeekend = React.useMemo(() => {
    const recurrence = recurrences[0];
    if (recurrence.__typename !== 'WeeklyRecurrenceType') {
      return false;
    }

    return recurrence.days.length === WEEKENDS.length && recurrence.days.every((day) => WEEKDAYS.includes(day));
  }, [recurrences]);

  const baseFrequencyOptions = React.useMemo(() => {
    if (recurrenceType === 'DailyRecurrenceType') {
      return DAILY_OPTIONS;
    }

    if (recurrenceType === 'WeeklyRecurrenceType') {
      if (isWeekend) {
        return EVERY_WEEKEND_OPTIONS;
      }
      return WEEKLY_OPTIONS;
    }

    if (recurrenceType === 'YearlyRecurrenceType') {
      return YEARLY_OPTIONS;
    }

    if (recurrenceType === 'SingleRecurrenceType') {
      return YEARLY_OPTIONS;
    }

    return MONTHLY_OPTIONS;
  }, [recurrenceType, isWeekDay, isWeekend]);

  const frequencyOptions = React.useMemo(
    () => (allowCustomFrequency ? [...baseFrequencyOptions, CUSTOM_FREQUENCY_OPTION] : baseFrequencyOptions),
    [baseFrequencyOptions, allowCustomFrequency]
  );

  const lengthOptions = React.useMemo(() => {
    if (recurrenceType === 'YearlyRecurrenceType' || recurrenceType === 'SingleRecurrenceType') {
      return YEARLY_LENGTH_OPTIONS;
    }

    // Filter length options based on availability restriction
    if (availabilityRestrictionDays) {
      return LENGTH_OPTIONS.filter((option) => {
        // Always allow n/a and this_week
        if (option.value === LengthOptionEnum.NA || option.value === LengthOptionEnum.THIS_WEEK) {
          return true;
        }
        // Map length options to approximate days
        const lengthInDays: Record<string, number> = {
          [LengthOptionEnum.ONE_WEEK]: 7,
          [LengthOptionEnum.ONE_MONTH]: 30,
          [LengthOptionEnum.THREE_MONTHS]: 90,
          [LengthOptionEnum.SIX_MONTHS]: 180,
          [LengthOptionEnum.TWELVE_MONTHS]: 365,
        };
        const optionDays = lengthInDays[option.value] || 0;
        return optionDays <= availabilityRestrictionDays;
      });
    }

    return LENGTH_OPTIONS;
  }, [recurrenceType, availabilityRestrictionDays]);

  const beginOptions = React.useMemo(() => {
    if (recurrenceType === 'YearlyRecurrenceType' || recurrenceType === 'SingleRecurrenceType') {
      return YEARLY_BEGIN_OPTIONS;
    }

    if (recurrenceType === 'DailyRecurrenceType') {
      return DAILY_BEGIN_OPTIONS;
    }

    if (recurrenceType === 'WeeklyRecurrenceType') {
      return WEEKLY_BEGIN_OPTIONS;
    }

    return MONTHLY_BEGIN_OPTIONS;
  }, [recurrenceType]);

  const [frequencyValue, setFrequencyValue] = React.useState<FREQUENCY_ENUM>(frequencyOptions[0].value);
  const [lengthValue, setLengthValue] = React.useState<LengthOptionEnum>(LengthOptionEnum.NA);
  const [dayFilterValue, setDayFilterValue] = React.useState<RecurrenceEnum[]>([]);
  const [beginFilterValue, setBeginFilterValue] = React.useState<BeginOptionsEnum>(beginOptions[0].value);
  const [customFrequency, setCustomFrequency] = React.useState<CustomFrequencyValue | null>(null);
  const [showCustomDialog, setShowCustomDialog] = React.useState(false);
  const [previousFrequency, setPreviousFrequency] = React.useState<FREQUENCY_ENUM>(frequencyValue);

  const onChangeFrequency = (newValue: FrequencyValueType) => {
    if (newValue.frequency === FREQUENCY_ENUM.CUSTOM && frequencyValue !== FREQUENCY_ENUM.CUSTOM) {
      setPreviousFrequency(frequencyValue);
      setShowCustomDialog(true);
    }
    setFrequencyValue(newValue.frequency);
    setLengthValue(newValue.length);
    setDayFilterValue(newValue.day);
    setBeginFilterValue(newValue.begin);
  };

  useDeepEffect(() => {
    const filteredOccurrences = occurrences.filter(filterByDay(dayFilterValue)).filter(filterByBegin(beginFilterValue));

    const maxDate = availabilityRestrictionDays
      ? DateTime.local()
          .plus({ days: availabilityRestrictionDays })
          .toISODate()
      : null;

    const frequencyFilter =
      frequencyValue === FREQUENCY_ENUM.CUSTOM && customFrequency
        ? filterByCustomFrequency(customFrequency)
        : filterByFrequence(frequencyValue);

    const preSelectedDates = filteredOccurrences
      .filter(frequencyFilter)
      .filter(filterInRange(lengthValue))
      .filter((date) => (maxDate ? date <= maxDate : true));

    onChange(preSelectedDates);
  }, [frequencyValue, lengthValue, dayFilterValue, beginFilterValue, availabilityRestrictionDays, customFrequency]);

  const onCloseCustomDialog = () => {
    setShowCustomDialog(false);
    if (!customFrequency) {
      setFrequencyValue(previousFrequency);
    }
  };

  React.useEffect(() => {
    if (value.length === 0 && occurrences.length > 0) {
      onChange(occurrences.slice(0, 1));
    }
  }, []);

  return (
    <Box width={'100%'}>
      <FrequencySelect
        frequencyOptions={frequencyOptions}
        lengthOptions={lengthOptions}
        dayOptions={DAY_OPTIONS}
        beginOptions={beginOptions}
        value={{
          frequency: frequencyValue,
          length: lengthValue,
          day: dayFilterValue,
          begin: beginFilterValue,
        }}
        onChange={onChangeFrequency}
      />
      {frequencyValue === FREQUENCY_ENUM.CUSTOM && customFrequency && (
        <Box display='flex' alignItems='center' marginBottom={1} style={{ gap: 8 }}>
          <Typography style={{ fontSize: 13 }}>{describeCustomFrequency(customFrequency)}</Typography>
          <Button
            size='small'
            color='primary'
            onClick={() => setShowCustomDialog(true)}
            style={{ textTransform: 'none', minWidth: 0 }}
          >
            Edit
          </Button>
        </Box>
      )}
      {allowCustomFrequency && (
        <CustomFrequencyDialog
          open={showCustomDialog}
          onClose={onCloseCustomDialog}
          value={customFrequency || DEFAULT_CUSTOM_FREQUENCY}
          onChange={(newCustomFrequency) => {
            setCustomFrequency(newCustomFrequency);
            setShowCustomDialog(false);
          }}
        />
      )}
      <Box display='flex'>
        <Box display='flex' alignItems='center' style={{ gap: '1rem' }}>
          <Legend color='#0374D5' label='Available' />
          <Legend color='#0374D533' label='Existing Availability' />
          <Legend color='#D7DDE2' label='Unavailable' />
        </Box>
      </Box>
      <Box marginTop={2} width='100%'>
        <DateSelect
          startDate={startDate}
          value={value}
          onChange={onChange}
          multiple
          occurrences={occurrences}
          availabilities={availabilities}
          unavailabilities={unavailabilities}
          availabilityRestrictionDays={availabilityRestrictionDays}
        />
      </Box>
    </Box>
  );
}
